import React from "react";
import { Text, UnorderedList, ListItem, Link, VStack } from "@chakra-ui/react";
import { LegalPageShell, LegalSection } from "./legal/LegalPageShell";

const EMAIL = "privacy@manage360.com";

const PrivacyPolicy = () => {
  return (
    <LegalPageShell
      badge="Legal"
      title="Privacy Policy"
      intro="We built Manage360 so you can organise your life in one place. Here is a clear picture of what we collect, why we use it, and how we keep it safe—with plain language you can actually read."
      lastUpdated={`Last updated: February 2026 · Questions? ${EMAIL}`}
    >
      <VStack align="stretch" spacing={10}>
        <LegalSection title="Who we are">
          <Text mb={4}>
            Manage360 is offered by <strong>Our Software Solutions Pvt. Ltd.</strong> This policy
            describes how we handle information when you use our website and app (together, the “service”).
          </Text>
          <Text>
            Using the service means you trust us with some of your data. We take that seriously and only use
            it to run and improve the product—in line with this policy and applicable law.
          </Text>
        </LegalSection>

        <LegalSection title="What information we collect">
          <Text mb={4}>Depending on how you use Manage360, we may process:</Text>
          <UnorderedList spacing={3} pl={1}>
            <ListItem>
              <strong>Account details</strong> — name, email address, phone number, and how you signed in
              (for example phone OTP, email, or Google). We use these to create your account and keep it
              secure.
            </ListItem>
            <ListItem>
              <strong>Content you add</strong> — notes, files you upload, portfolio and transaction-style
              entries you type in, project and todo items, daily chore lists, calendar connection data you
              configure, and group-expense information. This is stored so you can view and manage it in the
              dashboard.
            </ListItem>
            <ListItem>
              <strong>Voice assistant usage</strong> — when you use voice on supported screens, we process
              what you say (and the assistant’s reply) to run the feature. Token-based limits may apply;
              see Pricing in the app for buying extra AI Usage Credits where offered.
            </ListItem>
            <ListItem>
              <strong>Technical data</strong> — things like device type, approximate logs, and cookies or
              similar technologies needed for sign-in, preferences, and reliability. We keep this minimal.
            </ListItem>
          </UnorderedList>
        </LegalSection>

        <LegalSection title="How we use your information">
          <Text mb={4}>We use the data above to:</Text>
          <UnorderedList spacing={3} pl={1}>
            <ListItem>Provide the service: log you in, sync your workspace, and deliver the features you see in the app.</ListItem>
            <ListItem>Send important notices (for example security alerts or changes to this policy) when needed.</ListItem>
            <ListItem>Improve Manage360: fix bugs, understand feature usage in aggregate, and keep the platform stable.</ListItem>
            <ListItem>
              Work with trusted providers (such as hosting, email, SMS, or payments) who help us operate the
              service under strict confidentiality and only for the purpose we give them.
            </ListItem>
          </UnorderedList>
          <Text mt={4}>
            We do not sell your personal information to data brokers, and we do not use it to build
            unrelated advertising profiles.
          </Text>
        </LegalSection>

        <LegalSection title="How we protect your data">
          <Text mb={4}>
            We use reasonable technical and organisational measures—such as access controls, encryption in
            transit where appropriate for modern web apps, and separation of environments—to reduce the risk of
            unauthorised access, loss, or misuse.
          </Text>
          <Text>
            No online service can promise perfect security. If we learn of a breach that affects you in a
            meaningful way, we will act in line with applicable laws and notify you when required.
          </Text>
        </LegalSection>

        <LegalSection title="Sharing">
          <Text mb={4}>We may share information only when:</Text>
          <UnorderedList spacing={3} pl={1}>
            <ListItem>You ask us to—for example inviting a collaborator to a shared workspace feature.</ListItem>
            <ListItem>A vendor helps us run the service (hosting, messaging, payments), under contract and confidentiality.</ListItem>
            <ListItem>We must comply with law, court orders, or defend our legal rights.</ListItem>
          </UnorderedList>
        </LegalSection>

        <LegalSection title="Your choices and rights">
          <Text mb={4}>
            Where the law applies, you may have the right to access, correct, export, or delete certain
            personal data, or to object to some processing. Reach out and we will respond within a reasonable
            time.
          </Text>
          <Text>
            To exercise your rights or ask a privacy question, email{" "}
            <Link href={`mailto:${EMAIL}`} color="teal.500" fontWeight="semibold">
              {EMAIL}
            </Link>
            . Please include the email or phone associated with your account so we can verify you safely.
          </Text>
        </LegalSection>

        <LegalSection title="Retention">
          <Text>
            We keep your information only as long as your account is active and as needed to operate the
            service, meet legal obligations, or resolve disputes. When data is no longer needed, we delete or
            anonymise it in line with our internal schedules.
          </Text>
        </LegalSection>

        <LegalSection title="Children">
          <Text>
            Manage360 is intended for adults who can enter a binding agreement. We do not knowingly collect
            personal data from children below the age where parental consent is required in your region.
            If you believe a child has signed up, contact us and we will investigate and remove the account
            where appropriate.
          </Text>
        </LegalSection>

        <LegalSection title="International users">
          <Text>
            Our servers and subprocessors may be located in regions other than where you live. By using the
            service, you understand that your data may be processed in those locations, always with safeguards
            consistent with this policy and applicable law.
          </Text>
        </LegalSection>

        <LegalSection title="Changes to this policy">
          <Text mb={4}>
            We may update this Privacy Policy as the product or the law evolves. When we make important
            changes, we will post the new version here and update the “Last updated” date.
          </Text>
          <Text>
            Continuing to use Manage360 after changes means you accept the updated policy. If you disagree,
            you can stop using the service and request account closure.
          </Text>
        </LegalSection>
      </VStack>
    </LegalPageShell>
  );
};

export default PrivacyPolicy;
